import { toast } from 'sonner';
import {
  forwardSubject, forwardedHtml, formatMailbox, htmlToPlain, parseAddressList, quotedOriginalHtml, replyRecipients,
  replySubject, escapeHtml, applyTemplate, type DomainTemplateEntry, type ComposePayload, type OutgoingAttachment, type MessageDetail, type SharedPreferences, type CrmTask,
} from '@dts/core';
import { api, errorMessage } from './api';
import { useUi, type ComposerState } from './store';
import { queryClient } from './query-client';
import { qk } from './queries';
import { runCrmTask } from './crm';

function prefs(): SharedPreferences {
  return queryClient.getQueryData<SharedPreferences>(qk.prefs) ?? {};
}

function myAddresses(): string[] {
  const me = queryClient.getQueryData<{ user: { email: string }; addresses: { email: string }[] }>(qk.me);
  return me ? [me.user.email, ...me.addresses.map((a) => a.email)].map((a) => a.toLowerCase()) : [];
}

export function signatureFor(from: string | undefined): string {
  const sigs = prefs().signatures ?? {};
  return (from && sigs[from.toLowerCase()]) || sigs['*'] || '';
}

export function withSignature(html: string, from: string | undefined): string {
  const sig = signatureFor(from);
  return sig ? `${html}<p></p><div data-signature="1">${sig}</div>` : html;
}

function defaultFrom(d?: MessageDetail): string | undefined {
  const mine = myAddresses();
  const p = prefs();
  // Reply from the address the message was sent to, so multi-domain identities stay consistent.
  if (d) {
    const candidates = [d.summary.recipientAddress, ...parseAddressList(d.summary.to).map((m) => m.address), ...parseAddressList(d.summary.cc).map((m) => m.address)];
    const hit = candidates.find((a) => a && mine.includes(a.toLowerCase()));
    if (hit) return hit.toLowerCase();
  }
  // Otherwise the address the composer shows in From (its first enabled address), so the signature saved for that
  // address is the one inserted. Without this, new messages only ever got the "all addresses" signature.
  return p.defaultFrom ?? firstIdentity();
}

function firstIdentity(): string | undefined {
  const me = queryClient.getQueryData<{ addresses: { email: string; enabled: boolean }[] }>(qk.me);
  return me?.addresses.find((a) => a.enabled)?.email.toLowerCase();
}

function bodyHtml(d: MessageDetail): string {
  return d.htmlBody ?? `<p>${escapeHtml(d.textBody ?? '').replace(/\n/g, '<br>')}</p>`;
}

function crmDefault(): boolean {
  const mode = prefs().crm?.mode ?? 'off';
  return mode !== 'off';
}

export function composeNew(init: Partial<ComposerState> = {}) {
  const from = init.from ?? defaultFrom();
  return useUi.getState().openComposer({
    from,
    html: withSignature(init.html ?? '', from),
    logToCrm: crmDefault(),
    ...init,
  });
}

export function composeWithAi(instruction?: string, to: string[] = []) {
  return composeNew({ aiFirst: !instruction, autoAi: instruction, to });
}

export function composeReply(d: MessageDetail, all: boolean, aiInstruction?: string) {
  const from = defaultFrom(d);
  const { to, cc } = replyRecipients(d, myAddresses(), all);
  return useUi.getState().openComposer({
    mode: all ? 'replyAll' : 'reply',
    from,
    to: to.map(formatMailbox),
    cc: cc.map(formatMailbox),
    showCc: cc.length > 0,
    subject: replySubject(d.summary.subject),
    html: withSignature('', from),
    sourceId: d.summary.id,
    quoteHtml: quotedOriginalHtml(d, bodyHtml(d)),
    autoAi: aiInstruction,
    logToCrm: crmDefault(),
  });
}

export function composeForward(d: MessageDetail) {
  const from = defaultFrom(d);
  const files = d.attachments.map((a, i) => ({ a, index: i + 1 })).filter((x) => !x.a.inline);
  const id = useUi.getState().openComposer({
    mode: 'forward',
    from,
    subject: forwardSubject(d.summary.subject),
    html: withSignature('', from),
    sourceId: d.summary.id,
    quoteHtml: forwardedHtml(d, bodyHtml(d)),
    attachments: files.map(({ a, index }) => ({ id: `fwd-${index}`, fileName: a.fileName, contentType: a.contentType, size: a.sizeBytes, loading: true })),
    logToCrm: false,
  });
  // Pull the original attachments so they are forwarded too.
  files.forEach(({ index }) => {
    api.attachmentBase64(d.summary.id, index)
      .then((b64) => useUi.getState().updateComposer(id, (c) => ({ attachments: c.attachments.map((x) => (x.id === `fwd-${index}` ? { ...x, base64: b64, loading: false } : x)) })))
      .catch(() => useUi.getState().updateComposer(id, (c) => ({ attachments: c.attachments.filter((x) => x.id !== `fwd-${index}`) })));
  });
  return id;
}

export function composeFromDraft(d: MessageDetail) {
  const draft = d.draft ?? {};
  return useUi.getState().openComposer({
    mode: 'draft',
    draftId: d.summary.id,
    from: draft.from,
    to: draft.to ?? [],
    cc: draft.cc ?? [],
    bcc: draft.bcc ?? [],
    showCc: !!(draft.cc?.length || draft.bcc?.length),
    subject: draft.subject ?? '',
    html: draft.html ?? `<p>${escapeHtml(draft.text ?? '')}</p>`,
    sourceId: draft.inReplyTo,
    includeQuote: false,
    logToCrm: crmDefault(),
  });
}

/** mailto:a@b.com?subject=..&cc=..&body=.. */
export function composeMailto(url: string) {
  try {
    const u = new URL(url);
    const to = decodeURIComponent(u.pathname).split(',').filter(Boolean);
    const p = u.searchParams;
    composeNew({
      to,
      cc: p.get('cc')?.split(',') ?? [],
      bcc: p.get('bcc')?.split(',') ?? [],
      showCc: !!(p.get('cc') || p.get('bcc')),
      subject: p.get('subject') ?? '',
      html: p.get('body') ? `<p>${escapeHtml(p.get('body')!).replace(/\n/g, '<br>')}</p>` : '',
    });
  } catch {
    composeNew();
  }
}

/** Turns data: images in the body into cid: inline parts so mail clients that block data URIs still show them. */
function embedInlineImages(html: string): { html: string; parts: OutgoingAttachment[] } {
  const parts: OutgoingAttachment[] = [];
  const out = html.replace(/(<img[^>]*?src=")data:(image\/[a-z0-9.+-]+);base64,([^"]+)(")/gi, (_m, pre: string, type: string, b64: string, post: string) => {
    const cid = `img${parts.length + 1}.${Date.now().toString(36)}@dtsmail`;
    parts.push({ fileName: `image${parts.length + 1}.${type.split('/')[1].replace('+xml', '')}`, contentType: type, contentBase64: b64, contentId: cid, inline: true });
    return `${pre}cid:${cid}${post}`;
  });
  return { html: out, parts };
}

/** The sending domain's enabled template, if any. */
export function templateFor(from: string | undefined) {
  const domain = from?.split('@')[1]?.toLowerCase();
  const t = queryClient.getQueryData<DomainTemplateEntry[]>(['templates'])?.find((e) => e.domain === domain)?.template;
  return t?.enabled ? t : undefined;
}

export function brandedHtml(c: ComposerState, body: string): string {
  const t = c.brand ? templateFor(c.from) : undefined;
  if (!t) return body;
  const me = queryClient.getQueryData<{ user: { displayName: string } }>(qk.me);
  return applyTemplate(t.html, { content: body, senderName: me?.user.displayName, senderEmail: c.from, domain: c.from?.split('@')[1] });
}

export function toPayload(c: ComposerState): ComposePayload {
  const embedded = embedInlineImages(brandedHtml(c, c.includeQuote && c.quoteHtml ? c.html + c.quoteHtml : c.html));
  const html = embedded.html;
  return {
    from: c.from,
    to: c.to,
    cc: c.cc,
    bcc: c.bcc,
    subject: c.subject,
    html,
    text: htmlToPlain(html),
    inReplyTo: c.mode === 'reply' || c.mode === 'replyAll' ? c.sourceId : undefined,
    attachments: [
      ...c.attachments.filter((a) => a.base64).map((a) => ({ fileName: a.fileName, contentType: a.contentType || 'application/octet-stream', contentBase64: a.base64! })),
      ...embedded.parts,
    ],
    draftId: c.draftId,
  };
}

const UNDO_SECONDS = 6;

/** Closes the composer immediately, gives an undo window, then sends; restores the composer on undo or failure. */
export function sendWithUndo(c: ComposerState) {
  const ui = useUi.getState();
  ui.closeComposer(c.id);
  let undone = false;
  const restore = () => useUi.getState().openComposer({ ...c, minimized: false, rev: c.rev + 1 });
  const tid = toast('Sending…', {
    description: c.to.length ? `To ${c.to.slice(0, 2).join(', ')}${c.to.length > 2 ? ` +${c.to.length - 2}` : ''}` : undefined,
    duration: UNDO_SECONDS * 1000,
    action: {
      label: 'Undo',
      onClick: () => {
        undone = true;
        restore();
      },
    },
  });
  setTimeout(async () => {
    if (undone) return;
    toast.loading('Sending…', { id: tid });
    try {
      const payload = toPayload(c);
      const res = await api.send(payload);
      toast.success('Message sent', { id: tid, duration: 2500 });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: qk.folders });
      if (c.sourceId) queryClient.invalidateQueries({ queryKey: qk.thread(c.sourceId) });
      if (c.logToCrm && (prefs().crm?.mode ?? 'off') !== 'off') {
        const me = queryClient.getQueryData<{ user: { displayName: string } }>(qk.me);
        const task: CrmTask = {
          kind: 'log',
          userName: me?.user.displayName ?? 'the user',
          instructions: prefs().crm?.instructions,
          email: {
            direction: 'sent',
            from: c.from ?? '',
            to: c.to,
            cc: c.cc,
            subject: c.subject,
            date: new Date().toISOString(),
            body: payload.text ?? '',
          },
        };
        void runCrmTask(task, `Logging “${c.subject || 'email'}” to CRM`, res.id);
      }
    } catch (e) {
      toast.error(`Couldn't send: ${errorMessage(e)}`, { id: tid, duration: 8000 });
      restore();
    }
  }, UNDO_SECONDS * 1000);
}
